<script setup lang="ts">
    import { computed, onBeforeUnmount, ref, watchEffect } from 'vue';
    import { InputRegexValidator } from '@/shared/helpers/inputRegexValidator.ts';
    import VueMarkdown from './VueMarkdown.vue';

    const props = defineProps({
        modelValue: {
            type: String,
            required: true
        },
        showPreview: {
            type: Boolean,
            default: true
        },
        regexValidator: {
            type: InputRegexValidator,
            default: null
        }
    });

    const emits = defineEmits<{
        'update:modelValue': [value: string];
    }>();

    function onInput(value: string) {
        emits('update:modelValue', value);
    }

    const validationResult = computed(() => {
        return props.regexValidator?.validate(props.modelValue)?.error;
    });

    const validInput = computed(() => {
        return !props.regexValidator || !validationResult.value;
    });

    const fieldType = computed(() => {
        return validationResult.value ? 'is-danger' : '';
    });

    // Refs for scroll sync between editor and preview.
    const editorWrapperRef = ref<HTMLElement | null>(null);
    const previewRef = ref<HTMLElement | null>(null);

    let syncing = false;
    let detachListeners: (() => void) | null = null;

    function syncScroll(source: HTMLElement, target: HTMLElement) {
        if (syncing) return;
        const srcMax = source.scrollHeight - source.clientHeight;
        const dstMax = target.scrollHeight - target.clientHeight;
        if (srcMax <= 0 || dstMax <= 0) return;
        syncing = true;
        target.scrollTop = (source.scrollTop / srcMax) * dstMax;
        // Release on next frame so the resulting scroll event from `target` is ignored.
        requestAnimationFrame(() => { syncing = false; });
    }

    watchEffect(() => {
        // Tear down previous listeners whenever preview visibility changes.
        if (detachListeners) {
            detachListeners();
            detachListeners = null;
        }

        if (!props.showPreview) return;

        // Defer until DOM is in place.
        requestAnimationFrame(() => {
            const wrapper = editorWrapperRef.value;
            const preview = previewRef.value;
            if (!wrapper || !preview) return;
            const textarea = wrapper.querySelector('textarea') as HTMLTextAreaElement | null;
            if (!textarea) return;

            const onEditorScroll = () => syncScroll(textarea, preview);
            const onPreviewScroll = () => syncScroll(preview, textarea);
            textarea.addEventListener('scroll', onEditorScroll, { passive: true });
            preview.addEventListener('scroll', onPreviewScroll, { passive: true });

            detachListeners = () => {
                textarea.removeEventListener('scroll', onEditorScroll);
                preview.removeEventListener('scroll', onPreviewScroll);
            };
        });
    });

    onBeforeUnmount(() => {
        if (detachListeners) detachListeners();
        detachListeners = null;
    });

    defineExpose({ validInput });
</script>

<template>
    <div class="markdown-editor-root">
        <div class="columns is-variable is-2 mb-0 markdown-editor-row">
            <div
                ref="editorWrapperRef"
                :class="props.showPreview ? 'column is-half' : 'column is-full'">
                <b-field
                    :type="fieldType"
                    :message="validationResult"
                    :class="{ 'markdown-editor-pane': props.showPreview }">
                    <b-input
                        :model-value="props.modelValue"
                        type="textarea"
                        @update:model-value="onInput" />
                </b-field>
            </div>
            <div
                v-if="props.showPreview"
                class="column is-half">
                <div
                    ref="previewRef"
                    class="content markdown-editor-pane markdown-editor-preview">
                    <vue-markdown
                        :source="props.modelValue"
                        :html-enabled="false" />
                </div>
            </div>
        </div>
        <p
            v-if="props.showPreview"
            class="has-text-grey has-text-right mt-0">
            <b-icon
                size="is-small"
                icon="information-outline"
                class="mr-1" />
            <a
                href="/Help/markdown"
                target="_blank"><b>Markdown</b> supported</a> with preview alongside.
        </p>
    </div>
</template>

<style scoped>
    /*
     * The component fills 100% of the height provided by its parent.
     * Consumers control the height by sizing the surrounding container
     * (e.g. the modal body or a wrapper div with `height: 60vh`).
     */
    .markdown-editor-root {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
    }

    .markdown-editor-row {
        flex: 1 1 auto;
        min-height: 0;
    }

    .markdown-editor-pane {
        height: 100%;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    .markdown-editor-pane :deep(.control) {
        flex: 1 1 auto;
        min-height: 0;
        display: flex;
    }

    .markdown-editor-pane :deep(textarea) {
        flex: 1 1 auto;
        height: 100%;
        max-height: 100%;
        resize: none;
    }

    .markdown-editor-preview {
        border: 1px solid #dbdbdb;
        border-radius: 4px;
        padding: 0.75rem;
        background-color: #fff;
        overflow: auto;
    }
</style>
